<template>
  <div class="battle-pass-container">
    <div class="pass-header">
      <div class="header-content">
        <h1 class="pass-title">RivoBit BattlePass</h1>
        <div class="season-info">
          <span class="season-badge">SEASON 1</span>
          <span class="time-remaining">
            <i class="fas fa-clock"></i> Ends in: {{ daysRemaining }} days
          </span>
        </div>
      </div>
      <div class="premium-indicator" :class="{ 'active': isPremium }">
        {{ isPremium ? 'PREMIUM ACTIVE' : 'FREE PASS' }}
      </div>
    </div>

    <div class="pass-content">
      <div class="progress-section">
        <div class="level-display">
          <div class="current-level">LEVEL {{ currentLevel }}</div>
          <div class="level-progress">
            <div class="progress-bar" :style="{ width: progress + '%' }"></div>
            <div class="progress-text">{{ currentXP }} / {{ xpRequired }} XP ({{ progress }}%)</div>
          </div>
          <div class="max-level">MAX: {{ totalLevels }}</div>
        </div>
      </div>

      <div class="rewards-section">
        <div class="section-header">
          <h3>LEVEL REWARDS</h3>
          <div class="view-toggle">
            <button :class="{ 'active': viewMode === 'free' }" @click="viewMode = 'free'">Free</button>
            <button :class="{ 'active': viewMode === 'premium' }" @click="viewMode = 'premium'">Premium</button>
          </div>
        </div>

        <div class="rewards-grid">
          <div v-for="(reward, index) in filteredRewards" :key="index" class="reward-card" :class="{ 'locked': reward.locked, 'premium': reward.isPremium }">
            <div class="reward-tier">TIER {{ reward.level }}</div>
            <div class="reward-icon">
              <img :src="reward.icon" :alt="reward.name" />
              <div class="premium-badge" v-if="reward.isPremium">PREMIUM</div>
            </div>
            <div class="reward-details">
              <h4>{{ reward.name }}</h4>
              <p class="reward-amount">{{ reward.amount }}</p>
              <p class="reward-desc" v-if="reward.description">{{ reward.description }}</p>
            </div>
            <div class="reward-status">
              <span v-if="reward.claimed" class="claimed">
                <i class="fas fa-check-circle"></i> Claimed
              </span>
              <span v-else-if="!reward.locked" class="available">
                <i class="fas fa-gift"></i> Available
                <button class="claim-button" @click="claimReward(reward)">Claim</button>
              </span>
              <span v-else class="locked-status">
                <i class="fas fa-lock"></i> Locked
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="missions-section">
        <div class="section-header">
          <h3>MISSIONS</h3>
           <div class="missions-tabs">
    <div class="tabs">
      <button v-for="tab in tabs" :key="tab.key" :class="{ active: currentTab === tab.key }" @click="selectTab(tab.key)">
        {{ tab.label }}
      </button>
    </div>

    <VipCountdown v-if="activeResetTime" :targetTime="activeResetTime" />

    <div class="missions-list">
      <div v-for="mission in filteredMissions" :key="mission._id" class="mission-item">
        <img :src="resolveImage(mission.img)" alt="mission image" class="mission-img" />
        <div class="mission-content">
          <h4>{{ mission.name }}</h4>
          <p>{{ mission.description }}</p>
          <div class="mission-progress">
            <div class="progress-bar">
              <div :style="{ width: (mission.currentProgress / mission.targetValue) * 100 + '%' }"></div>
            </div>
            <span>{{ mission.currentProgress }}/{{ mission.targetValue }}</span>
          </div>
        </div>
        <div class="mission-reward">
          <span class="xp-reward">+{{ mission.xpReward }} XP</span>
          <span v-if="mission.tokenReward" class="token-reward">+{{ mission.tokenReward }} <img src="/token.png" alt="token" /></span>
          <button
            class="claim-button"
            :disabled="mission.isCompleted || mission.currentProgress < mission.targetValue"
            @click="claimMissionReward(mission)"
          >
            <span v-if="mission.isCompleted">Claimed</span>
            <span v-else>Claim</span>
          </button>
        </div>
      </div>
    </div>
  </div>
        </div>

        <div class="missions-list">
          <div v-for="(mission, index) in missions" :key="index" class="mission-item">
            <div class="mission-info">
              <div class="mission-icon">
                <i :class="mission.icon"></i>
              </div>
              <div class="mission-details">
                <h4>{{ mission.name }}</h4>
                <p>{{ mission.description }}</p>
                <div class="mission-progress">
                  <div class="progress-bar">
                    <div :style="{ width: (mission.current / mission.target) * 100 + '%' }"></div>
                  </div>
                  <span>{{ mission.current }}/{{ mission.target }}</span>
                </div>
              </div>
            </div>
            <div class="mission-reward">
              <span class="xp-reward">+{{ mission.xp }} XP</span>
              <span class="token-reward" v-if="mission.tokens">
                +{{ mission.tokens }} <img src="https://cryptologos.cc/logos/ethereum-eth-logo.png" alt="ETH" />
              </span>
              <button class="claim-button" :class="{ 'disabled': mission.current < mission.target || mission.lastClaimed }" @click="claimMission(mission)">Claim</button>
            </div>
          </div>
        </div>
      </div>

      <div class="upgrade-section" v-if="!isPremium">
        <div class="upgrade-content">
          <div class="upgrade-info">
            <h3>UPGRADE TO PREMIUM PASS</h3>
            <ul class="benefits-list">
              <li><i class="fas fa-check"></i> Unlock all premium rewards</li>
              <li><i class="fas fa-check"></i> +20% XP boost</li>
              <li><i class="fas fa-check"></i> Exclusive tournaments</li>
              <li><i class="fas fa-check"></i> Priority support</li>
            </ul>
          </div>
          <button class="upgrade-button" @click="upgradePass">
            UPGRADE NOW - 200USDT
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import VipCountdown from '@/components/VipCountdown.vue';

const backendUrl = process.env.VUE_APP_BACKEND_URL;

export default {
  name: 'ProfessionalBattlePass',
  components: { VipCountdown },
 
  data() {
    return {
      currentLevel: 0,
      currentXP: 0,
      xpRequired: 100,
      totalLevels: 20,
      isPremium: false,
      daysRemaining: 0,
      viewMode: 'free',
      resetTime: '8h 23m',
      rewards: [],
      currentTab: 'DAILY',
      tabs: [
        { label: 'Daily Missions', key: 'DAILY' },
        { label: 'Weekly Missions', key: 'WEEKLY' },
        { label: 'Seasonal Missions', key: 'SEASONAL' },
      ],
      missions: [],
      claimedRewardIds: [],
      resetTimestamp: null,
      activeTab: 'daily'
    };
  },
  computed: {

     filteredMissions() {
      return this.missions.filter(m => m.missionType === this.currentTab);
    },
    activeResetTime() {
      const nextReset = this.filteredMissions[0]?.nextReset;
      return nextReset ? new Date(nextReset) : null;
    },


    ...mapGetters(['authUser']),
    progress() {
      return Math.min(Math.round((this.currentXP / this.xpRequired) * 100), 100);
    },
    filteredRewards() {
      let filtered = this.viewMode === 'free'
        ? this.rewards.filter(r => !r.isPremium)
        : this.rewards.filter(r => r.isPremium);

      return filtered.map(r => {
        const claimed = this.claimedRewardIds.includes(r._id);
        const locked = r.level > this.currentLevel;
        return {
          ...r,
          claimed,
          locked,
          name: (this.viewMode === 'premium' && !this.isPremium) ? 'Premium Reward' : r.name,
          description: (this.viewMode === 'premium' && !this.isPremium) ? 'Upgrade to unlock' : r.description,
          amount: (this.viewMode === 'premium' && !this.isPremium) ? '???' : r.amount
        };
      });
    }
  },
  methods: {
    async fetchBattlePassData() {
      try {
        const res = await axios.get('/battlepass/status', {
          headers: { 'x-auth-token': localStorage.getItem('token') }
        });
        const { season, progress, missions, claimedRewardIds, rewards } = res.data;

        this.totalLevels = season.totalLevels;
        this.daysRemaining = this.calculateDaysRemaining(season.endDate);
        this.currentLevel = progress.currentLevel;
        this.isPremium = progress.premium;
        this.claimedRewardIds = claimedRewardIds;
        this.currentXP = progress.currentXP;
        this.xpRequired = season.xpPerLevel[progress.currentLevel - 1] || 100;
        this.resetTimestamp = season.endDate;

        this.rewards = rewards.map(r => ({
          _id: r._id,
          name: r.rewardType,
          amount: r.amount,
          icon: r.img ? `${backendUrl}${r.img}` : 'https://cdn-icons-png.flaticon.com/512/2489/2489737.png',
          isPremium: r.isPremium,
          description: r.description,
          level: r.level
        }));

        this.missions = missions.map(m => ({
          name: m.name,
          description: m.description,
          icon: 'fas fa-tasks',
          current: m.currentProgress || 0,
          target: m.targetValue,
          xp: m.xpReward,
          tokens: m.tokenReward,
          missionId: m._id,
          isCompleted: m.isCompleted,
          lastClaimed: m.lastClaimed || false
        }));
      } catch (error) {
        console.error('Battle Pass verisi alınamadı:', error);
      }
    },

    async claimReward(reward) {
      try {
        const res = await axios.post('/battlepass/claim-battlepass-reward', {
          rewardId: reward._id
        }, {
          headers: { 'x-auth-token': localStorage.getItem('token') }
        });

        this.$toast?.success?.(res.data.message || 'Reward claimed!');
        this.claimedRewardIds.push(reward._id);

        if (res.data.newBalance !== undefined) {
          this.$store.commit('auth_update_user', {
            ...this.authUser.user,
            balance: res.data.newBalance
          });
        }

        this.fetchBattlePassData();
      } catch (err) {
        this.$toast?.error?.(err.response?.data?.message || 'Error claiming reward');
      }
    },

     async claimMissionReward(mission) {
      try {
        const res = await axios.post('/battlepass/claim-mission-reward', {
          missionId: mission._id
        }, {
          headers: { 'x-auth-token': localStorage.getItem('token') }
        });

        this.$toast?.success?.(res.data.message || 'Mission reward claimed!');
        this.$emit('refresh'); // refresh missions in parent
      } catch (err) {
        this.$toast?.error?.(err.response?.data?.message || 'Claim failed.');
      }
    },

    async upgradePass() {
      try {
        const res = await axios.post('/battlepass/buy-premium', {}, {
          headers: { 'x-auth-token': localStorage.getItem('token') }
        });

        this.isPremium = true;
        this.$emit('pass-upgraded');
        this.$toast?.success?.(res.data.message || 'Premium pass activated');

        if (res.data.user) {
          this.$store.commit('auth_update_user', res.data.user);
        }

        this.fetchBattlePassData();
      } catch (err) {
        this.$toast?.error?.(err.response?.data?.message || 'Insufficient balance');
      }
    },

     selectTab(tab) {
      this.currentTab = tab;
    },
    resolveImage(path) {
      return process.env.VUE_APP_BACKEND_URL + path;
    },

    calculateDaysRemaining(endDate) {
      const now = new Date();
      const end = new Date(endDate);
      const diffTime = end - now;
      return Math.max(Math.ceil(diffTime / (1000 * 60 * 60 * 24)), 0);
    }
  },
  mounted() {
    this.fetchBattlePassData();
  }
};
</script>




<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@400;500;600;700&display=swap');
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css');

.battle-pass-container {
  font-family: 'Rajdhani', sans-serif;
  color: #fff;
      background: linear-gradient(135deg, #1a2a3a 0%, #0f172a 100%);
    border-radius: 8px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  overflow: hidden;
  max-width: 1400px;
  
  margin: 0 auto;
}

.pass-header {
 
  padding: 20px 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
      background: rgba(0, 0, 0, 0.2);
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 8px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

.header-content {
  display: flex;
  flex-direction: column;
}

.pass-title {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: 1px;
  background: linear-gradient(to bottom, #01e0a3 0%, #00aa6d 95%, transparent 60%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.season-info {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-top: 8px;
}

.season-badge {
  background: linear-gradient(to bottom, #01e0a3 0%, #00aa6d 95%, transparent 60%), linear-gradient(to bottom, #3a6d5f, #3a6d5f);
  color: white;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
}

.time-remaining {
  color: #a1a1c2;
  font-size: 14px;
}

.premium-indicator {
  background: var(--card3-bg);
  color: #a1a1c2;
  padding: 8px 16px;
  border-radius: 4px;
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 1px;
}

.premium-indicator.active {
  background: linear-gradient(to bottom, #01e0a3 0%, #00aa6d 95%, transparent 60%), linear-gradient(to bottom, #3a6d5f, #3a6d5f);
  color: white;
  box-shadow: 0 0 15px rgba(5, 235, 235, 0.5);
}

.pass-content {
  padding: 30px;
}

.progress-section {
  background: #161626;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 30px;
  border: 1px solid var(--card3-bg);
}

.level-display {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.current-level, .max-level {
  font-size: 18px;
  font-weight: 600;
  color: #01e0a3;
}

.level-progress {
  flex-grow: 1;
  margin: 0 20px;
  height: 20px;
  background: var(--card3-bg);
  border-radius: 10px;
  position: relative;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background:linear-gradient(to bottom, #01e0a3 0%, #00aa6d 95%, transparent 60%);
  border-radius: 10px;
  transition: width 0.5s ease;
}

.progress-text {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  color: white;
  font-weight: 600;
}

.level-steps {
  display: flex;
  justify-content: space-between;
  position: relative;
}

.level-steps::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 2px;
  background: var(--card3-bg);
  z-index: 0;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 1;
}

.step-number {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  margin-bottom: 8px;
}

.step.completed .step-number {
  background: #01e0a3;
  color: white;
  box-shadow: 0 0 10px rgba(106, 90, 205, 0.7);
}

.step.current .step-number {
  background: white;
  color: #01e0a3;
  border: 3px solid #01e0a3;
  font-weight: 700;
}

.step.locked .step-number {
  background: var(--card3-bg);
  color: #a1a1c2;
  border: 2px solid #3a3a5a;
}

.step-connector {
  width: 100%;
  height: 2px;
  background: var(--card3-bg);
}

.step.completed .step-connector {
  background: #01e0a3;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--card3-bg);
}

.section-header h3 {
  margin: 0;
  font-size: 20px;
  color: #01e0a3;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.view-toggle {
  display: flex;
  gap: 10px;
}

.view-toggle button {
  background: var(--card3-bg);
  border: none;
  color: #a1a1c2;
  padding: 6px 12px;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.view-toggle button.active {
  background-Image:
        linear-gradient(to bottom, #01e0a3 0%, #00aa6d 95%, transparent 60%), linear-gradient(to bottom, #3a6d5f, #3a6d5f);
    
    background-Repeat: no-repeat;
    background-Size: 100% 100%;
    border-Radius: 5px;
  color: white;
}

.rewards-grid {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding-bottom: 10px;
}

.missions-tabs {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
}
.missions-tabs button {
  background: var(--card3-bg);
  color: #a1a1c2;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
}
.missions-tabs button.active {
   background-image:
        linear-gradient(to bottom, #01e0a3 0%, #00aa6d 95%, transparent 60%), linear-gradient(to bottom, #3a6d5f, #3a6d5f);
   
    background-Repeat: no-repeat;
    background-Size: 100% 100%;
    border-Radius: 5px;
  color: white;
}

/* Countdown görünümü */
.countdown-container {
  font-size: 14px;
  color: #ffc107;
  display: flex;
  align-items: center;
  gap: 6px;
}
.countdown-container i {
  color: #ffc107;
}

.reward-card {
  background: #161626;
  border-radius: 8px;
  padding: 15px;
  border: 1px solid var(--card3-bg);
  transition: all 0.3s;
  flex: 0 0 auto;
  width: 180px;
}

.reward-card.premium {
  border-left: 4px solid #9370db;
}

.reward-card.locked {
  opacity: 0.6;
}

.reward-tier {
  font-size: 12px;
  color: #a1a1c2;
  margin-bottom: 10px;
}

.reward-icon {
  position: relative;
  width: 60px;
  height: 60px;
  margin: 0 auto 15px;
}

.reward-icon img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.premium-badge {
  position: absolute;
  top: -10px;
  right: -10px;
  background: #9370db;
  color: white;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 10px;
  font-weight: 600;
}

.reward-details h4 {
  margin: 0 0 5px;
  font-size: 16px;
  color: white;
}

.reward-amount {
  color: #01e0a3;
  font-weight: 600;
  margin: 0 0 5px;
}

.reward-desc {
  color: #a1a1c2;
  font-size: 12px;
  margin: 0;
}

.reward-status {
  margin-top: 15px;
  font-size: 12px;
  font-weight: 600;
}

.reward-status .claimed {
  color: #4caf50;
}

.reward-status .available {
  color: #ffc107;
}

.reward-status .locked-status {
  color: #a1a1c2;
}

.missions-list {
  display: grid;
  gap: 15px;
}

.mission-item {
  background: #161626;
  border-radius: 8px;
  padding: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid var(--card3-bg);
}

.mission-info {
  display: flex;
  align-items: center;
  gap: 15px;
}

.mission-icon {
  width: 40px;
  height: 40px;
  background: var(--card3-bg);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #01e0a3;
  font-size: 18px;
}

.mission-details h4 {
  margin: 0 0 5px;
  font-size: 16px;
  color: white;
}

.mission-details p {
  margin: 0;
  color: #a1a1c2;
  font-size: 13px;
}

.mission-progress {
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.mission-progress .progress-bar {
  flex-grow: 1;
  height: 6px;
  background: var(--card3-bg);
  border-radius: 3px;
  overflow: hidden;
}

.mission-progress .progress-bar > div {
  height: 100%;
  background: linear-gradient(to bottom, #01e0a3 0%, #00aa6d 95%, transparent 60%);
  border-radius: 3px;
}

.mission-progress span {
  font-size: 12px;
  color: #a1a1c2;
}

.mission-reward {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 5px;
}

.xp-reward {
  color: #01e0a3;
  font-weight: 600;
  font-size: 14px;
}

.token-reward {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #ffc107;
  font-weight: 600;
  font-size: 14px;
}

.token-reward img {
  width: 16px;
  height: 16px;
}

.claim-button {
  background-image:
        linear-gradient(to bottom, #01e0a3 0%, #00aa6d 95%, transparent 60%), linear-gradient(to bottom, #3a6d5f, #3a6d5f);
   
    background-Repeat: no-repeat;
    background-Size: 100% 100%;
    border-Radius: 5px;
  color: white;
  border: none;
  padding: 6px 12px;
  
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.claim-button.disabled {
  background-image:
        linear-gradient(to bottom, #061612 0%, #05110d 95%, transparent 60%), linear-gradient(to bottom, #3a6d5f, #3a6d5f);
   
    background-Repeat: no-repeat;
    background-Size: 100% 100%;
    border-Radius: 5px;
  color: #a1a1c2;
  cursor: not-allowed;
}

.upgrade-section {
  background: linear-gradient(135deg, #1a1a3a 0%, #0d0d1f 100%);
  border-radius: 8px;
  padding: 20px;
  margin-top: 30px;
  
}

.upgrade-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.upgrade-info h3 {
  margin: 0 0 10px;
  color: white;
  font-size: 20px;
}

.benefits-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.benefits-list li {
  margin-bottom: 5px;
  color: #a1a1c2;
  font-size: 14px;
}

.benefits-list li i {
  color: #01e0a3;
  margin-right: 8px;
}

.upgrade-button {
  background-image:
        linear-gradient(to bottom, #01e0a3 0%, #00aa6d 95%, transparent 60%), linear-gradient(to bottom, #3a6d5f, #3a6d5f);
   
    background-Repeat: no-repeat;
    background-Size: 100% 100%;
    border-Radius: 5px;
  color: white;
  border: none;
  padding: 12px 24px;
  
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.upgrade-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(106, 90, 205, 0.5);
}

@media (max-width: 768px) {
  .pass-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .upgrade-content {
    flex-direction: column;
    gap: 20px;
  }
  
  .mission-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .mission-reward {
    width: 100%;
    align-items: flex-start;
  }
}
</style>